import { Boxes, CircleDollarSign, EyeOff, KeyRound, PiggyBank, PowerOff, RefreshCw, TrendingUp, Wallet } from 'lucide-react';
import { formatCount, formatPercent, humanUser } from '@/shared/analytics/MetricFormatting';
import type { AnomalyEvent } from '@/shared/analytics/AnalyticsTypes';
import { alertsSample, kpiSample, seriesSample, trendSample } from '@/shared/signals/SignalSampleData';
import type { SignalData, SignalDatum, SignalTileDef } from '@/shared/signals/SignalTypes';
import { platformIdForProviderType, platformLabel } from '@/shared/ProviderPlatform';
import type { ISignalLicenseSource } from '@/shared/signals/SignalTypes';
import type { LicenseChurn, LicensePool, PlatformSpend } from './LicensesApiService';

const WEEK_LABELS = ['8w', '7w', '6w', '5w', '4w', '3w', '2w', 'now'] as const;
const SNAPSHOT_TTL_MS = 60_000;
const DAY_MS = 24 * 60 * 60 * 1000;
const INACTIVITY_DAYS = 30;

/** A cross-pool roll-up of the live license portfolio, the basis for the seat & cost KPIs. */
interface LicenseSnapshot {
    pools: LicensePool[];
    total: number;
    assigned: number;
    available: number;
    idle: number;
    /** The tenant's reporting currency (ISO 4217), derived from the cost-summary rows. */
    currency: string;
    /** Per-connection trailing-30-day consumption spend rows from cost-summary. */
    spendRows: PlatformSpend[];
    perSeatMonthly: number;
    reclaimMonthly: number;
    holdCostMonthly: number;
    /** Seat churn (reclaimed / assigned) over the optimization window, carried on the same optimization read. */
    churn: LicenseChurn;
}

/** Compact currency formatter honoring the tenant's reporting currency, falling back gracefully for unknown ISO codes. */
function compactMoney(currency: string, value: number): string {
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }).format(value);
    } catch {
        return `${new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value)} ${currency}`;
    }
}

let snapshotCache: { at: number; data: LicenseSnapshot } | null = null;
let snapshotInflight: Promise<LicenseSnapshot> | null = null;

/**
 * Loads pools + optimization + trailing-30-day spend once and folds them into a
 * {@link LicenseSnapshot}, shared across the seat/cost KPIs so the four live tiles
 * don't each re-hit the Licenses API. Single-flighted with a short TTL.
 */
/** Total spend in the dominant reporting currency only — never sums across mixed currencies. */
function sumDominantCurrency(spendRows: PlatformSpend[]): { currency: string; total: number } {
    const currency = spendRows.find((row) => row.currency.length > 0)?.currency ?? 'USD';
    const total = spendRows.filter((row) => row.currency === currency).reduce((sum, row) => sum + row.totalAmount, 0);
    return { currency, total };
}

async function licenseSnapshot(api: ISignalLicenseSource, inactivityDays: number): Promise<LicenseSnapshot> {
    if (snapshotCache !== null && Date.now() - snapshotCache.at < SNAPSHOT_TTL_MS) return snapshotCache.data;
    if (snapshotInflight !== null) return snapshotInflight;

    snapshotInflight = (async (): Promise<LicenseSnapshot> => {
        const [optimization, pools] = await Promise.all([api.getOptimization(inactivityDays), api.listPools()]);
        const total = pools.reduce((sum, pool) => sum + pool.totalSeats, 0);
        const assigned = pools.reduce((sum, pool) => sum + pool.assignedSeats, 0);
        const available = pools.reduce((sum, pool) => sum + pool.availableSeats, 0);
        const idle = optimization.platforms.reduce((sum, platform) => sum + platform.idle, 0);

        const end = new Date();
        const start = new Date(end.getTime() - 30 * DAY_MS);
        const spendRows = await api.getPlatformSpend(start.toISOString(), end.toISOString()).catch(() => []);
        const { currency, total: spendTotal } = sumDominantCurrency(spendRows);

        const perSeatMonthly = assigned > 0 ? spendTotal / assigned : 0;
        const churn = optimization.churn ?? { reclaimed: 0, assigned: 0, windowDays: inactivityDays };
        const data: LicenseSnapshot = {
            pools,
            total,
            assigned,
            available,
            idle,
            currency,
            spendRows,
            perSeatMonthly,
            reclaimMonthly: perSeatMonthly * idle,
            holdCostMonthly: perSeatMonthly * (idle + available),
            churn,
        };
        snapshotCache = { at: Date.now(), data };
        snapshotInflight = null;
        return data;
    })();

    try {
        return await snapshotInflight;
    } catch (cause) {
        snapshotInflight = null;
        throw cause;
    }
}

const SHADOW_SEAT_TEMPLATES = [
    { label: 'Consumer ChatGPT on corp identity', detail: '3 users · Engineering', severity: 'high' as const },
    { label: 'Unmanaged Claude.ai login', detail: 'Marketing', severity: 'medium' as const },
    { label: 'Personal Gemini access', detail: 'Finance', severity: 'medium' as const },
    { label: 'Unsanctioned Perplexity Pro', detail: '2 users · Legal', severity: 'low' as const },
];

const PROVIDER_LABELS = ['OpenAI', 'Anthropic', 'Google Gemini', 'Microsoft Copilot', 'Meta'] as const;

/**
 * The Licenses landing dashboard. Seat-buffer, idle, savings, and hold-cost KPIs
 * are live from the Licenses API (pools / optimization / cost-summary); the
 * provider split is live from pool inventory. Holding period is live too — a
 * weekly trend of seat bindings from each assignment's `assignedAt` (it counts
 * currently-active seats only, so seats bound-then-revoked aren't reflected;
 * the assignments table is a current-state snapshot, not an event log). Churn is
 * live too, from the `license_assignment_events` log now written on every
 * assign/unassign/reclaim — reclaimed and assigned counts over the optimization
 * window, carried on the shared optimization read (no extra request). Shadow-seat detection is live as well
 * — a telemetry × license-assignment cross-reference (case-normalized) that
 * flags users active in the analytics gold layer with no managed seat, refined
 * against the IDP directory to confirm corporate identity when that dataset is
 * published (and falling back to the unrefined superset when it isn't).
 */
const ALL_LICENSE_SIGNALS: SignalTileDef[] = [
    {
        id: 'available-licenses',
        title: 'Available Licenses',
        description:
            'The real-time buffer between purchased enterprise seats and active assignments — so admins are alerted before the available pool hits zero and onboarding stalls.',
        icon: KeyRound,
        accent: 'indigo',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 120, max: 480, format: 'count', caption: 'unassigned seats in the pool', deltaPct: [-8, 14], goodWhen: 'up', spark: 'up' }),
        live: async ({ licenses, tuning }): Promise<SignalData> => {
            const s = await licenseSnapshot(licenses, tuning.inactivityDays);
            return {
                headline: formatCount(s.available),
                format: 'count',
                caption: s.total > 0 ? `${formatPercent(s.available / s.total)} of ${formatCount(s.total)} purchased seats free` : 'No seats configured',
            };
        },
    },
    {
        id: 'provider-spend-30d',
        title: 'Trailing 30-Day Spend',
        description: 'Consumption spend per seat-based provider over the trailing 30 days — where the AI budget actually lands.',
        icon: CircleDollarSign,
        accent: 'teal',
        kind: 'bars',
        sample: (rng) => seriesSample(rng, PROVIDER_LABELS, { min: 400, max: 9000, format: 'currency' }),
        live: async ({ licenses, tuning }): Promise<SignalData> => {
            const s = await licenseSnapshot(licenses, tuning.inactivityDays);
            const rows = s.spendRows.filter((row) => row.currency === s.currency);
            if (rows.length === 0) throw new Error('no provider spend in the trailing 30 days');
            const labelByConnection = new Map<string, string>();
            for (const pool of s.pools) {
                const platformId = platformIdForProviderType(pool.providerTypeId);
                labelByConnection.set(
                    pool.providerConnectionId,
                    platformId !== null ? platformLabel(platformId) : pool.providerDisplayName || pool.providerTypeId,
                );
            }
            const series: SignalDatum[] = rows
                .map((row) => ({ label: labelByConnection.get(row.providerConnectionId) ?? row.providerConnectionId, value: row.totalAmount }))
                .sort((a, b) => b.value - a.value);
            return { series, format: 'currency' };
        },
    },
    {
        id: 'inactive-licenses',
        title: 'Inactive Licenses',
        description: `Assigned seats with zero telemetry for ${INACTIVITY_DAYS}+ working days, normalized against approved leave — the immediate candidates for reclamation.`,
        icon: PowerOff,
        accent: 'amber',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 30, max: 140, format: 'count', caption: `idle ${INACTIVITY_DAYS}+ working days`, deltaPct: [-18, 6], goodWhen: 'down', spark: 'down' }),
        live: async ({ licenses, tuning }): Promise<SignalData> => {
            const s = await licenseSnapshot(licenses, tuning.inactivityDays);
            return {
                headline: formatCount(s.idle),
                format: 'count',
                caption: s.assigned > 0 ? `${formatPercent(s.idle / s.assigned)} of ${formatCount(s.assigned)} assigned seats idle` : 'No assignments',
            };
        },
    },
    {
        id: 'reclaimed-savings',
        title: 'Savings from Reclaimed Licenses',
        description: 'The capital recycled back into the unassigned pool by running automated seat-recovery on inactive users.',
        icon: PiggyBank,
        accent: 'teal',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 4000, max: 22000, format: 'currency', caption: 'recovered this month', deltaPct: [3, 28], goodWhen: 'up', spark: 'up' }),
        live: async ({ licenses, tuning }): Promise<SignalData> => {
            const s = await licenseSnapshot(licenses, tuning.inactivityDays);
            return {
                headline: compactMoney(s.currency, s.reclaimMonthly),
                format: 'currency',
                caption: `est. monthly value of ${formatCount(s.idle)} reclaimable seats`,
            };
        },
    },
    {
        id: 'excess-hold-cost',
        title: 'Cost to Hold Excess Licenses',
        description: 'The monthly cost of maintaining an inventory of excess seats — idle assignments plus the unassigned overhang.',
        icon: Wallet,
        accent: 'rose',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 3000, max: 18000, format: 'currency', caption: 'monthly carry on excess seats', deltaPct: [-10, 12], goodWhen: 'down', spark: 'flat' }),
        live: async ({ licenses, tuning }): Promise<SignalData> => {
            const s = await licenseSnapshot(licenses, tuning.inactivityDays);
            const excess = s.idle + s.available;
            return {
                headline: compactMoney(s.currency, s.holdCostMonthly),
                format: 'currency',
                caption: `${formatCount(excess)} excess seats × ${compactMoney(s.currency, s.perSeatMonthly)}/seat`,
            };
        },
    },
    {
        id: 'holding-period',
        title: 'License Holding Period',
        description: 'The rate at which newly purchased seats get actively bound to users over time — a read on deployment momentum and capacity planning.',
        icon: TrendingUp,
        accent: 'violet',
        kind: 'trend',
        sample: (rng) => trendSample(rng, WEEK_LABELS, 220, 920, 'up'),
        live: async ({ licenses }): Promise<SignalData> => {
            const assignments = await licenses.listAllAssignments();
            const now = Date.now();
            const byBucket = new Map<number, number>();
            let inWindow = 0;
            for (const assignment of assignments) {
                const boundAt = Date.parse(assignment.assignedAt);
                if (Number.isNaN(boundAt)) continue;
                const weeksAgo = Math.floor((now - boundAt) / (7 * DAY_MS));
                if (weeksAgo < 0 || weeksAgo >= WEEK_LABELS.length) continue;
                const index = WEEK_LABELS.length - 1 - weeksAgo;
                byBucket.set(index, (byBucket.get(index) ?? 0) + 1);
                inWindow += 1;
            }
            if (inWindow === 0) throw new Error('no recent seat bindings to chart');
            const series: SignalDatum[] = WEEK_LABELS.map((label, index) => ({ label, value: byBucket.get(index) ?? 0 }));
            return { series, format: 'count' };
        },
    },
    {
        id: 'multi-provider-users',
        title: 'Multi-Provider Users',
        description: 'How license seats distribute across disparate AI providers — visibility into vendor concentration and multi-model diversification.',
        icon: Boxes,
        accent: 'cyan',
        kind: 'donut',
        sample: (rng) => seriesSample(rng, PROVIDER_LABELS, { min: 40, max: 300, format: 'count' }),
        live: async ({ licenses, tuning }): Promise<SignalData> => {
            const s = await licenseSnapshot(licenses, tuning.inactivityDays);
            const byProvider = new Map<string, SignalDatum>();
            for (const pool of s.pools) {
                const existing = byProvider.get(pool.providerTypeId) ?? { label: pool.providerDisplayName.length > 0 ? pool.providerDisplayName : pool.providerTypeId, value: 0 };
                existing.value += pool.assignedSeats;
                byProvider.set(pool.providerTypeId, existing);
            }
            const series = [...byProvider.values()].filter((datum) => datum.value > 0).sort((a, b) => b.value - a.value);
            if (series.length === 0) throw new Error('no provider seats to chart');
            return { series, format: 'count' };
        },
    },
    {
        id: 'shadow-seat-detection',
        title: 'Shadow Seat Detection',
        description: 'Cross-references gateway telemetry against the IDP directory to surface employees on unmanaged, consumer-grade AI accounts under corporate identities.',
        icon: EyeOff,
        accent: 'rose',
        kind: 'alerts',
        sample: (rng) => alertsSample(rng, SHADOW_SEAT_TEMPLATES, [4, 17]),
        live: async ({ gateway, licenses, filters, tuning }): Promise<SignalData> => {
            const [usage, assignments] = await Promise.all([
                gateway.aggregate({ source: 'interactions', groupBy: ['user_id'], metrics: ['turns'], filters }),
                licenses.listAllAssignments(),
            ]);
            const licensed = new Set(
                assignments.map((assignment) => String(assignment.userId).trim().toLowerCase()).filter((user) => user.length > 0),
            );
            const directoryUsers = await gateway.directoryUserIds().catch((): string[] => []);
            const corporate = new Set(directoryUsers.map((user) => user.trim().toLowerCase()));
            const shadow = usage.rows
                .flatMap((row) => {
                    const user = row.keys.user_id;
                    if (user === undefined || user === '—' || user.trim().length === 0) return [];
                    const normalized = user.trim().toLowerCase();
                    if (licensed.has(normalized)) return [];
                    if (corporate.size > 0 && !corporate.has(normalized)) return [];
                    return [{ user, turns: row.metrics.turns }];
                })
                .sort((a, b) => b.turns - a.turns);
            if (shadow.length === 0) throw new Error('no shadow seats detected');
            const alerts: AnomalyEvent[] = shadow.slice(0, tuning.anomalyLimit).map((entry) => ({
                label: 'Unlicensed AI activity',
                detail: `${humanUser(entry.user)} · ${formatCount(entry.turns)} prompts`,
                severity: entry.turns >= tuning.shadowSeatHighTurns ? 'high' : entry.turns >= tuning.shadowSeatMediumTurns ? 'medium' : 'low',
            }));
            return { alerts, alertCount: shadow.length };
        },
    },
    {
        id: 'license-churn',
        title: 'License Churn',
        description: 'Seat turnover in the active window — seats reclaimed for inactivity or role changes, alongside the seats newly assigned over the same period.',
        icon: RefreshCw,
        accent: 'indigo',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 5, max: 24, format: 'count', caption: 'seats reclaimed & assigned', deltaPct: [-6, 20], goodWhen: 'up', spark: 'up' }),
        live: async ({ licenses, tuning }): Promise<SignalData> => {
            const { churn } = await licenseSnapshot(licenses, tuning.inactivityDays);
            if (churn.reclaimed + churn.assigned === 0) {
                return { headline: '0', format: 'count', caption: `no seats reclaimed or assigned (${churn.windowDays}d)` };
            }
            return {
                headline: formatCount(churn.reclaimed),
                format: 'count',
                caption: `reclaimed · ${formatCount(churn.assigned)} assigned (${churn.windowDays}d)`,
            };
        },
    },
];

/** The three hard-dollar cost tiles — surfaced under the Spend › Cost section, not the Licenses page. */
const COST_SIGNAL_IDS = new Set(['provider-spend-30d', 'reclaimed-savings', 'excess-hold-cost']);

/** Cost tiles for the Spend › Cost section (trailing spend, reclaimed savings, excess hold cost). */
export const LICENSE_COST_SIGNALS: SignalTileDef[] = ALL_LICENSE_SIGNALS.filter((signal) => COST_SIGNAL_IDS.has(signal.id));

/** Seat-lifecycle tiles that stay with the Licenses page (availability, inactivity, churn, holding period, provider spread, shadow seats). */
export const LICENSE_SIGNALS: SignalTileDef[] = ALL_LICENSE_SIGNALS.filter((signal) => !COST_SIGNAL_IDS.has(signal.id));
